import * as React from 'react';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronsUpDown, Check, GraduationCap, BookOpen, Shield } from 'lucide-react';
import VERSIONS from '@assets/js/versions';
import { useOrganization } from '@clerk/clerk-react';

const roleIcons = {
  student: GraduationCap,
  teacher: BookOpen,
  admin: Shield,
};

const roleLabels = {
  student: 'Student',
  teacher: 'Teacher',
  admin: 'Admin',
};

export function TeamSwitcher({ selectedRole, setSelectedRole }) {
  const { membership } = useOrganization();
  const memberRole = membership?.roleName?.toLowerCase();
  const appVersion = VERSIONS[process.env.REACT_APP_VERSION];

  // Define available roles based on member role
  const getAvailableRoles = (memberRole) => {
    switch (memberRole) {
      case 'member':
        return ['student']; // Fixed to student only
      case 'admin':
        return ['student', 'teacher', 'admin']; // All roles
      case 'staff':
        return ['student', 'teacher']; // Staff and student
      default:
        return ['student']; // Default fallback
    }
  };

  const availableRoles = getAvailableRoles(memberRole);

  // Validate and fallback for selectedRole based on available roles
  const validRole = selectedRole && roleIcons[selectedRole] && availableRoles.includes(selectedRole)
    ? selectedRole
    : availableRoles[0];

  const SelectedIcon = roleIcons[validRole];
  const isZenshkool = appVersion.brand === 'Zenshkool';
  const showBrandLogo = validRole === 'student' || !isZenshkool;

  // If member role only allows one option, don't show dropdown
  const showDropdown = availableRoles.length > 1;

  // If not Zenshkool, show static brand header without dropdown
  if (!isZenshkool) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" className="cursor-default">
            <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
              <img src={appVersion.url} alt="logo" className="rounded-lg" />
            </div>
            <div className="grid flex-1 text-left text-lg leading-tight">
              <span className="truncate font-semibold">{appVersion.brand}</span>
              <span className="truncate text-xs">by brogrammers</span>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        {showDropdown ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                  {showBrandLogo ? (
                    <img src={appVersion.url} alt="logo" className="rounded-lg" />
                  ) : (
                    <SelectedIcon className="size-4" />
                  )}
                </div>
                <div className="grid flex-1 text-left text-lg leading-tight">
                  <span className="truncate font-semibold">
                    {showBrandLogo ? appVersion.brand : roleLabels[validRole]}
                  </span>
                  <span className="truncate text-xs">by brogrammers</span>
                </div>
                <ChevronsUpDown className="ml-auto" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-(--radix-dropdown-menu-trigger-width)" align="start">
              {availableRoles.map((role) => {
                const RoleIcon = roleIcons[role];
                return (
                  <DropdownMenuItem key={role} onSelect={() => setSelectedRole(role)}>
                    <RoleIcon className="mr-2 size-4" />
                    {roleLabels[role]}
                    {role === validRole && <Check className="ml-auto size-4" />}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <SidebarMenuButton size="lg" className="cursor-default">
            <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
              {showBrandLogo ? (
                <img src={appVersion.url} alt="logo" className="rounded-lg" />
              ) : (
                <SelectedIcon className="size-4" />
              )}
            </div>
            <div className="grid flex-1 text-left text-lg leading-tight">
              <span className="truncate font-semibold">
                {showBrandLogo ? appVersion.brand : roleLabels[validRole]}
              </span>
              <span className="truncate text-xs">by brogrammers</span>
            </div>
          </SidebarMenuButton>
        )}
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
